// Filières Service - Institution-specific accredited programs management
// Fetches filières (accredited programs) from the Ministry of Higher Education

import { apiGet, apiPost, apiPatch, apiPut, apiDelete, apiPostMultipart } from '@/utils/api';
import type {
  Filiere,
  FiliereWithHierarchy,
  FiliereWithOptions,
  FiliereOption,
  FiliereForStudentYear,
  Semester,
  SemesterWithModules,
  Module,
  ModuleElement,
  FiliereChefWithUser,
  AvailableChef,
  FiliereUploadSummary,
  FiliereUploadHistory,
  CreateFiliereData,
  UpdateFiliereData,
  CreateModuleData,
  UpdateModuleData,
  CreateElementData,
  UpdateElementData,
  UpdateElementWeightsData,
  CampusInfo,
  CampusBasic,
  FiliereCampusAssignment,
  FiliereCampusLink,
  UpdateFiliereCampusAssignmentData,
  CreateFiliereWithCampusData,
  DeleteImpact,
  DeleteImpactTarget,
} from '@/types/filiere.types';

// Re-export types for backward compatibility
export type NomenclatureFiliere = Filiere;

export interface UploadResult {
  success: boolean;
  message: string;
  totalRecords: number;
  created: number;
  skipped: number;
  warnings?: string[];
}

export type { FiliereUploadHistory } from '@/types/filiere.types';

// Cache for filières
let filieresCache: NomenclatureFiliere[] | null = null;

/**
 * Fetch all filières for the current tenant
 * This is a PUBLIC endpoint - students can fetch without full authentication
 * @param forceRefresh - Force refresh from API even if cached
 * @param activeOnly - If false, fetch all filières including inactive ones
 * @param _tenantId - Optional explicit tenant ID (kept for backward compatibility, not used)
 */
export async function fetchFilieres(forceRefresh = false, activeOnly = false, _tenantId?: string): Promise<NomenclatureFiliere[]> {
  if (filieresCache && !forceRefresh) {
    return filieresCache;
  }

  try {
    const data = await apiGet<NomenclatureFiliere[]>(`/filieres?activeOnly=${activeOnly}`);
    filieresCache = data;
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des filières:', error);
    // Return cached data or empty array - don't break the form
    return filieresCache || [];
  }
}

/**
 * Get a specific filière by code
 */
export async function getFiliereByCode(code: string): Promise<NomenclatureFiliere | null> {
  try {
    return await apiGet<NomenclatureFiliere>(`/filieres/code/${encodeURIComponent(code)}`);
  } catch (error) {
    console.error('Erreur lors de la récupération de la filière:', error);
    // Return null for 404 errors
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Upload filières from Excel file
 * Expected columns: établissement, diplôme, code filière, filière, nombre d'années, année d'accréditation
 */
export async function uploadFilieresExcel(file: File): Promise<UploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<UploadResult>('/filieres/upload', formData);

  // Clear cache to force refresh
  filieresCache = null;

  return result;
}

/**
 * Get upload history for filières
 */
export async function fetchFilieresUploadHistory(): Promise<FiliereUploadHistory[]> {
  try {
    return await apiGet<FiliereUploadHistory[]>('/filieres/uploads/history');
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'historique:', error);
    return [];
  }
}

/**
 * Toggle filière active status
 */
export async function toggleFiliereStatus(id: string): Promise<NomenclatureFiliere> {
  const result = await apiPatch<NomenclatureFiliere>(`/filieres/${id}/toggle`);

  // Clear cache
  filieresCache = null;

  return result;
}

/**
 * Delete a filière
 */
export async function deleteFiliere(id: string): Promise<void> {
  await apiDelete<void>(`/filieres/${id}`);

  // Clear cache
  filieresCache = null;
}

/**
 * Clear cache (call after modifications)
 */
export function clearFilieresCache(): void {
  filieresCache = null;
}

/**
 * Get cached filières synchronously (if available)
 */
export function getFilieresSync(): NomenclatureFiliere[] {
  return filieresCache || [];
}

// ==================== NEW API FUNCTIONS ====================

/**
 * Get filières visible to current user (filtered for Chef de Filière)
 */
export async function fetchMyFilieres(activeOnly = true): Promise<Filiere[]> {
  return apiGet<Filiere[]>(`/filieres/my-filieres?activeOnly=${activeOnly}`);
}

/**
 * Get a filière by ID with full hierarchy (semesters, modules, elements)
 */
export async function getFiliereById(id: string): Promise<FiliereWithHierarchy> {
  return apiGet<FiliereWithHierarchy>(`/filieres/${id}`);
}

/**
 * Create a filière manually
 */
export async function createFiliere(data: CreateFiliereData): Promise<Filiere> {
  const result = await apiPost<Filiere>('/filieres', data);
  filieresCache = null;
  return result;
}

/**
 * Update a filière
 */
export async function updateFiliere(id: string, data: UpdateFiliereData): Promise<Filiere> {
  const result = await apiPatch<Filiere>(`/filieres/${id}`, data);
  filieresCache = null;
  return result;
}

/**
 * Update the specialisation start semester for a filière
 */
export async function updateSpecialisationStart(filiereId: string, startSemester: number): Promise<Filiere> {
  const result = await apiPatch<Filiere>(`/filieres/${filiereId}/specialisation-start`, { startSemester });
  filieresCache = null;
  return result;
}

/**
 * Add an option (spécialisation) to an existing parent filière
 */
export async function addFiliereOption(filiereId: string, code: string, name: string): Promise<Filiere> {
  const result = await apiPost<Filiere>(`/filieres/${filiereId}/options`, { code, name });
  filieresCache = null;
  return result;
}

/**
 * Rename an option of a filière
 */
export async function renameFiliereOption(filiereId: string, optionId: string, name: string): Promise<Filiere> {
  const result = await apiPatch<Filiere>(`/filieres/${filiereId}/options/${optionId}`, { name });
  filieresCache = null;
  return result;
}

/**
 * Archive an option of a filière (refused while it still has modules)
 */
export async function archiveFiliereOption(filiereId: string, optionId: string): Promise<Filiere> {
  const result = await apiDelete<Filiere>(`/filieres/${filiereId}/options/${optionId}`);
  filieresCache = null;
  return result;
}

/**
 * Archive a filière
 */
export async function archiveFiliere(id: string): Promise<Filiere> {
  const result = await apiPatch<Filiere>(`/filieres/${id}/archive`);
  filieresCache = null;
  return result;
}

/**
 * Restore an archived filière
 */
export async function restoreFiliere(id: string): Promise<Filiere> {
  const result = await apiPatch<Filiere>(`/filieres/${id}/restore`);
  filieresCache = null;
  return result;
}

/**
 * Upload filières with detailed summary
 */
export async function uploadFilieresWithSummary(file: File): Promise<FiliereUploadSummary> {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiPostMultipart<FiliereUploadSummary>('/filieres/upload-with-summary', formData);
  filieresCache = null;
  return result;
}

// ==================== DELETE IMPACT ====================

/**
 * Count what a destructive delete of an element / module / semester / option would wipe
 */
export async function getDeleteImpact(target: DeleteImpactTarget): Promise<DeleteImpact> {
  return apiGet<DeleteImpact>(`/filieres/impact?type=${target.type}&id=${target.id}`);
}

// ==================== SEMESTER FUNCTIONS ====================

/**
 * Get all semesters for a filière
 */
export async function getSemestersByFiliere(filiereId: string): Promise<SemesterWithModules[]> {
  return apiGet<SemesterWithModules[]>(`/filieres/${filiereId}/semesters`);
}

/**
 * Create the next semester for a filière
 */
export async function createSemester(filiereId: string): Promise<Semester> {
  return apiPost<Semester>(`/filieres/${filiereId}/semesters`);
}

/**
 * Delete a semester
 */
export async function deleteSemester(semesterId: string): Promise<void> {
  await apiDelete<void>(`/filieres/semesters/${semesterId}`);
}

// ==================== MODULE FUNCTIONS ====================

/**
 * Create a module in a semester
 */
export async function createModule(data: CreateModuleData): Promise<Module> {
  return apiPost<Module>('/filieres/modules', data);
}

/**
 * Update a module
 */
export async function updateModule(moduleId: string, data: UpdateModuleData): Promise<Module> {
  return apiPatch<Module>(`/filieres/modules/${moduleId}`, data);
}

/**
 * Delete a module
 */
export async function deleteModule(moduleId: string): Promise<void> {
  await apiDelete<void>(`/filieres/modules/${moduleId}`);
}

/**
 * Update element weights for a module
 */
export async function updateElementWeights(moduleId: string, data: UpdateElementWeightsData): Promise<void> {
  await apiPut<void>(`/filieres/modules/${moduleId}/weights`, data);
}

// ==================== ELEMENT FUNCTIONS ====================

/**
 * Create an element in a module
 */
export async function createElement(data: CreateElementData): Promise<ModuleElement> {
  return apiPost<ModuleElement>('/filieres/elements', data);
}

/**
 * Update an element
 */
export async function updateElement(elementId: string, data: UpdateElementData): Promise<ModuleElement> {
  return apiPatch<ModuleElement>(`/filieres/elements/${elementId}`, data);
}

/**
 * Delete an element
 */
export async function deleteElement(elementId: string): Promise<void> {
  await apiDelete<void>(`/filieres/elements/${elementId}`);
}

// ==================== CHEF DE FILIÈRE FUNCTIONS ====================

/**
 * Get all chefs assigned to a filière
 */
export async function getChefsByFiliere(filiereId: string): Promise<FiliereChefWithUser[]> {
  return apiGet<FiliereChefWithUser[]>(`/filieres/${filiereId}/chefs`);
}

/**
 * Get available chefs (not assigned to this filière)
 */
export async function getAvailableChefs(filiereId: string): Promise<AvailableChef[]> {
  return apiGet<AvailableChef[]>(`/filieres/${filiereId}/available-chefs`);
}

/**
 * Assign a chef de filière
 */
export async function assignChef(filiereId: string, userId: string): Promise<void> {
  await apiPost<void>(`/filieres/${filiereId}/chefs`, { userId });
}

/**
 * Remove a chef de filière
 */
export async function removeChef(filiereId: string, userId: string): Promise<void> {
  await apiDelete<void>(`/filieres/${filiereId}/chefs/${userId}`);
}

// ==================== FILIÈRE OPTION HELPERS ====================

/**
 * Option id a semester is tagged with, or null for tronc-commun semesters.
 * An option's matières live in the PARENT filière's semesters tagged this way.
 */
export function semesterOptionId(s: SemesterWithModules): string | null {
  return (s as { optionFiliereId?: string | null }).optionFiliereId ?? s.optionFiliere?.id ?? null;
}

/**
 * Options of a filière that apply at the given niveau, i.e. those with
 * option-tagged semesters among the niveau's two semester numbers.
 */
export function optionsForNiveau(
  filiere: FiliereWithOptions,
  semesters: SemesterWithModules[],
  niveau: number,
): FiliereOption[] {
  if (!filiere.options?.length) return [];
  const nums = [(niveau - 1) * 2 + 1, (niveau - 1) * 2 + 2];
  const tagged = new Set(
    semesters.filter((s) => nums.includes(s.number)).map(semesterOptionId).filter(Boolean),
  );
  return filiere.options.filter((o) => tagged.has(o.id));
}

// ==================== FILIÈRE OPTIONS FUNCTIONS ====================

/**
 * Get filières grouped with their options (tree structure)
 * Base filières are returned with their options nested
 */
export async function fetchFilieresGrouped(activeOnly = true, _tenantId?: string): Promise<FiliereWithOptions[]> {
  try {
    return await apiGet<FiliereWithOptions[]>(`/filieres/grouped?activeOnly=${activeOnly}`);
  } catch (error) {
    console.error('Erreur lors de la récupération des filières groupées:', error);
    return [];
  }
}

/**
 * Get filières for student selection based on year of study
 * - For early years: returns base filières only
 * - For last year: returns options (or base if no options exist)
 */
export async function fetchFilieresForStudentYear(
  niveauFormation: number,
  nombreAnnees: number,
  _tenantId?: string
): Promise<FiliereForStudentYear[]> {
  try {
    return await apiGet<FiliereForStudentYear[]>(
      `/filieres/for-student-year?niveauFormation=${niveauFormation}&nombreAnnees=${nombreAnnees}`
    );
  } catch (error) {
    console.error('Erreur lors de la récupération des filières par année:', error);
    return [];
  }
}

/**
 * Link existing filières to their parents based on code patterns
 * This is a one-time utility to fix existing data
 */
export async function linkExistingFiliereOptions(): Promise<{ updated: number }> {
  const result = await apiPost<{ updated: number }>('/filieres/link-options');
  filieresCache = null;
  return result;
}

// ==================== CAMPUS ASSIGNMENT FUNCTIONS ====================

/**
 * Get campus info for the institution (count and list)
 * Used to determine if we need to show campus selection UI
 */
export async function getCampusInfo(): Promise<CampusInfo> {
  return apiGet<CampusInfo>('/filieres/campus/info');
}

/**
 * Get all campuses for the institution (public endpoint)
 */
export async function getCampusList(_tenantId?: string): Promise<CampusBasic[]> {
  return apiGet<CampusBasic[]>('/filieres/campus/list');
}

/**
 * Campus links of every filière of the institution in one call
 */
export async function getAllFiliereCampusLinks(): Promise<FiliereCampusLink[]> {
  return apiGet<FiliereCampusLink[]>('/filieres/campus-assignments/all');
}

/**
 * Get campus assignments for a filière
 */
export async function getFiliereCampusAssignments(filiereId: string): Promise<FiliereCampusAssignment> {
  return apiGet<FiliereCampusAssignment>(`/filieres/${filiereId}/campus-assignments`);
}

/**
 * Update campus assignments for a filière
 */
export async function updateFiliereCampusAssignments(
  filiereId: string,
  data: UpdateFiliereCampusAssignmentData
): Promise<FiliereCampusAssignment> {
  return apiPut<FiliereCampusAssignment>(`/filieres/${filiereId}/campus-assignments`, data);
}

/**
 * Get filières available at a specific campus
 */
export async function getFilieresForCampus(
  campusId: string,
  activeOnly = true,
  _tenantId?: string
): Promise<Filiere[]> {
  return apiGet<Filiere[]>(`/filieres/by-campus/${campusId}?activeOnly=${activeOnly}`);
}

/**
 * Create a filière with campus assignment
 * This is an enhanced version that handles campus assignment during creation
 */
export async function createFiliereWithCampus(data: CreateFiliereWithCampusData): Promise<Filiere> {
  const result = await apiPost<Filiere>('/filieres', data);
  filieresCache = null;
  return result;
}
